import { useMemo } from 'react'

import { Select } from '@procurify/ui'

import {
  CheckboxCustomFieldValues,
  CustomFieldTypes,
} from '@webapp/core/entities/internal/CustomField'
import { useQueryCustomFieldValues } from '@webapp/core/features/get-custom-fields/models/useQueryCustomFieldValues'
import {
  type CustomFieldWithValueOptions,
  isCheckedCheckboxCustomFieldValue,
} from '@webapp/core/utils/customfields'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

interface IValueOption {
  value: string
  label: string
}

interface IValueSelectProps {
  inputId?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  error?: boolean
  /** Selected values as stored on the field: always an array, one item at most for a checkbox. */
  value: string[]
  onChange: (values: string[]) => void
}

export interface IBudgetCustomFieldValueSelectProps extends IValueSelectProps {
  customField: Nullable<CustomFieldWithValueOptions>
}

const toSelectedOptions = (values: string[], options: IValueOption[]) =>
  values.map(
    (selectedValue) =>
      options.find((option) => option.value === selectedValue) ?? {
        value: selectedValue,
        label: selectedValue,
      }
  )

const CheckboxValueSelect = ({
  inputId,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  error,
  value,
  onChange,
}: IValueSelectProps) => {
  const intl = useProcurifyIntl()
  const { checkedOption, uncheckedOption, options } = useMemo(() => {
    const checked: IValueOption = {
      value: CheckboxCustomFieldValues.CHECKED,
      label: intl.formatMessage(messages.checkboxCheckedLabel),
    }
    const unchecked: IValueOption = {
      value: CheckboxCustomFieldValues.UNCHECKED,
      label: intl.formatMessage(messages.checkboxUncheckedLabel),
    }

    return {
      checkedOption: checked,
      uncheckedOption: unchecked,
      options: [checked, unchecked],
    }
  }, [intl])

  const getSelectedOption = () => {
    const [storedValue] = value

    if (storedValue === undefined) return null
    if (isCheckedCheckboxCustomFieldValue(storedValue)) return checkedOption

    return uncheckedOption
  }

  const handleChange = (option: Nullable<IValueOption>) =>
    onChange(option ? [option.value] : [])

  return (
    <Select<IValueOption>
      inputId={inputId}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      error={error}
      options={options}
      value={getSelectedOption()}
      onChange={handleChange}
      isClearable
    />
  )
}

const DropdownValueSelect = ({
  inputId,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  error,
  customFieldId,
  value,
  onChange,
}: IValueSelectProps & { customFieldId: number }) => {
  const intl = useProcurifyIntl()
  const { values: valueOptions, isLoading } = useQueryCustomFieldValues({
    customFieldId,
  })
  const options = useMemo<IValueOption[]>(
    () =>
      valueOptions.map((valueOption) => ({
        value: valueOption.value,
        label: valueOption.isAvailable
          ? valueOption.value
          : intl.formatMessage(messages.inactiveValueLabel, {
              value: valueOption.value,
            }),
      })),
    [valueOptions, intl]
  )

  return (
    <Select<IValueOption, true>
      inputId={inputId}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      error={error}
      isMulti
      closeMenuOnSelect={false}
      isLoading={isLoading}
      options={options}
      value={toSelectedOptions(value, options)}
      onChange={(selectedOptions) =>
        onChange(selectedOptions.map((option) => option.value))
      }
    />
  )
}

/**
 * Values of a dropdown (multi-select) or checkbox (single Yes/No) custom
 * field, stored as the strings the backend saves. Disabled until a field is
 * chosen. Keyed by field id so moving between two fields of the same type
 * starts a fresh select.
 */
export const BudgetCustomFieldValueSelect = ({
  customField,
  ...props
}: IBudgetCustomFieldValueSelectProps) => {
  const intl = useProcurifyIntl()

  if (!customField) {
    return (
      <Select
        inputId={props.inputId}
        aria-describedby={props['aria-describedby']}
        options={[]}
        isDisabled
        placeholder={intl.formatMessage(messages.noCustomFieldPlaceholder)}
      />
    )
  }

  if (customField.type === CustomFieldTypes.CHECKBOX) {
    return <CheckboxValueSelect key={customField.id} {...props} />
  }

  return (
    <DropdownValueSelect
      key={customField.id}
      customFieldId={customField.id}
      {...props}
    />
  )
}
